import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { getFirstLetter, hideCardNumber, highlightSearchText, prettifyStars } from '@utils/index';
import FormField from '@components/form_field';
import { EContactType, EDdrStatus, TContact, TPayee } from '@xeppt/xeppt-sdk/types';
import { Icon, TIconType } from '@components/icons';
import styles from './styles.module.scss';
import Input from '@components/common/input';
import { useUserContext } from '@hooks/context/useUserContext';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';
import { useFormContext, useWatch } from 'react-hook-form';
import { apiContactService } from '@api';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useLocales } from '@hooks/helpers/useLocales';
import Skeleton from '@components/common/skeleton';
import { useTranslation } from 'react-i18next';
import Button from '@components/common/button';
import InfoBlock from '@components/common/info_block';
import { pageAnimation } from '@const/animation';
import { motion } from 'framer-motion';
import { EFundsOperation } from '@enum';
import Typography from '@components/common/typography';
import emptySearchImage from '@svg/illustrations/empty_contact_search.svg';
import { useDebounceValue } from 'usehooks-ts';
import Uploader from '@components/uploader';
import { useProcessEmailsCsv } from '@hooks/helpers/useProccessEmailsCsv';
import { async } from '@firebase/util';
import { validator } from '@xeppt/xeppt-sdk';
import { isValidEmail } from '@helpers/validation_objects';
import Switch from '@components/common/switch';
import Radio from '@components/common/radio';
import Phone from '@components/phone';

interface IProps {
    payeesList?: TPayee[];
    onSubmit: () => void;
    onPrev?: () => void;
    onChangeNewContact?: (val: boolean) => void;
    onChangeCustomContact?: (val: boolean) => void;
    method: EFundsOperation;
    contactsList?: TContact[];
    onChangeEmails?: Dispatch<SetStateAction<string[]>>;
}

const Recipient: FC<IProps> = ({
    contactsList,
    onSubmit,
    method,
    onPrev,
    payeesList,
    onChangeNewContact,
    onChangeCustomContact,
    onChangeEmails
}) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'send_and_request.send_money.info'
    });
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const { account } = useUserContext();
    const { control, trigger, setValue } = useFormContext();
    const watchedContact = useWatch({ name: 'contact', control });
    const watchedAccount = useWatch({ name: 'account', control });
    const watchedIsBulkGiftCards = useWatch({ name: 'isBulkGiftCards', control });
    const watchedGiftCardRecipientEmail = useWatch({ name: 'giftCardRecipientEmail', control });
    const { validationLocale, labelLocale, submitLocale, placeholderLocale } = useLocales();
    const [requestSearch, setRequestSearch] = useDebounceValue('', 500);
    const [isNewExternal, setIsNewExternal] = useState(false);
    const { processEmailsCsv } = useProcessEmailsCsv();
    const [recipientNotify, setRecipientNotify] = useState<'email' | 'phone'>('email');

    useEffect(() => {
        onChangeCustomContact && onChangeCustomContact(isNewExternal);
    }, [isNewExternal]);

    useEffect(() => {
        setRequestSearch(search);
    }, [search]);

    const isBank = useMemo(
        () =>
            method === EFundsOperation.EFT_CREDIT_WITHDRAWAL ||
            method === EFundsOperation.EFT_DEBIT_DEPOSIT,
        [method]
    );

    const isWallet = useMemo(
        () =>
            method === EFundsOperation.WALLET_CREDIT_TRANSFER ||
            method === EFundsOperation.WALLET_DEBIT_REQUEST,
        [method]
    );

    const isETransfer = useMemo(
        () =>
            method === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER ||
            method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        [method]
    );

    const isEps = useMemo(() => method === EFundsOperation.EPS_DEBIT_DEPOSIT, [method]);

    const isPayBills = useMemo(() => method === EFundsOperation.PAY_BILL_CREDIT_PAYMENT, [method]);

    const isRequest = useMemo(() => {
        return (
            method === EFundsOperation.WALLET_DEBIT_REQUEST ||
            method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST ||
            method === EFundsOperation.EPS_DEBIT_DEPOSIT ||
            method === EFundsOperation.EFT_DEBIT_DEPOSIT
        );
    }, [method]);

    const { data: searchContacts } = useApiQuery({
        method: () => apiContactService.searchContact(requestSearch),
        deps: [requestSearch],
        condition: requestSearch.length >= 3
    });

    const contacts = useMemo(() => {
        if (isBank) {
            return {
                contacts:
                    account?.bank_accounts
                        ?.filter((item) => {
                            return (
                                item.account_name?.toLowerCase().includes(search?.toLowerCase()) ||
                                item.branch_name?.toLowerCase().includes(search?.toLowerCase()) ||
                                item.account_number?.toLowerCase().includes(search?.toLowerCase())
                            );
                        })
                        .map((item) => ({
                            first_name: '',
                            last_name: item.branch_name,
                            email: `${item.account_name} ${prettifyStars(item.account_number)}`,
                            id: item.id
                        })) || []
            };
        }
        if (isEps) {
            return {
                contacts:
                    account?.eps_cards
                        ?.filter((item) => {
                            return (
                                item.card_holder_name
                                    ?.toLowerCase()
                                    .includes(search?.toLowerCase()) ||
                                item.card_brand?.toLowerCase().includes(search?.toLowerCase()) ||
                                item.card_number?.toLowerCase().includes(search?.toLowerCase())
                            );
                        })
                        .map((item) => ({
                            first_name: '',
                            last_name: item.card_holder_name,
                            email: `${item.card_brand} ${hideCardNumber(item.card_number, 'full')}`,
                            id: item.id
                        })) || []
            };
        }
        if (isPayBills) {
            return {
                contacts:
                    payeesList
                        ?.filter((item) => {
                            return (
                                item?.alias?.toLowerCase().includes(search?.toLowerCase()) ||
                                item?.name?.toLowerCase().includes(search?.toLowerCase()) ||
                                item?.account_number?.toLowerCase().includes(search?.toLowerCase())
                            );
                        })
                        .map((item) => ({
                            first_name: '',
                            last_name: item?.alias ? `${item.alias} (${item.name})` : item.name,
                            email: `${prettifyStars(item.account_number)}`,
                            id: item.id
                        })) || []
            };
        }
        if (search) {
            return {
                contacts:
                    searchContacts?.contacts
                        ?.map((item) => {
                            if (
                                (method === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER ||
                                    method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST) &&
                                !item.email
                            ) {
                                return item;
                            }
                            return {
                                ...item,
                                email: item.username ? `@${item.username}` : item.email
                            };
                        })
                        ?.filter((item) => {
                            if (
                                (method === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER ||
                                    method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST) &&
                                !item.email
                            ) {
                                return false;
                            }
                            return true;
                        }) || [],
                suggestions: searchContacts?.suggestions?.filter(() => {
                    if (
                        method === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER ||
                        method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST
                    ) {
                        return false;
                    }
                    return true;
                })
            };
        }
        if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) {
            return {
                contacts: [],
                suggestions: []
            };
        }
        return {
            contacts:
                contactsList
                    ?.filter((item) => {
                        if (
                            (method === EFundsOperation.E_TRANSFER_CREDIT_TRANSFER ||
                                method === EFundsOperation.E_TRANSFER_DEBIT_REQUEST) &&
                            item.type === EContactType.INTERNAL &&
                            !item.email
                        )
                            return false;
                        return true;
                    })
                    .map((item) => {
                        if (!item?.email) {
                            return {
                                ...item,
                                email: `@${item.username}`
                            };
                        } else {
                            return item;
                        }
                    }) || []
        };
    }, [
        contactsList,
        search,
        isBank,
        account,
        watchedContact,
        isETransfer,
        payeesList,
        searchContacts
    ]);

    const foundContact = useMemo(() => {
        if (contacts) {
            //@ts-ignore
            return contacts?.contacts?.find(
                (item: any) => item.id === watchedContact || item.id === watchedAccount
            );
        }
        return undefined;
    }, [watchedContact, contacts]);

    console.log({ contacts, foundContact });

    const { data: ddrContactStatus, isLoading: isContactDdrLoading } = useApiQuery({
        method: () => apiContactService.checkContactDDR(watchedContact),
        deps: [watchedContact],
        condition: isETransfer && watchedContact && !isRequest
    });

    const handleSubmit = () => {
        trigger().then((isValid) => {
            if (isValid) {
                onSubmit();
            }
        });
    };

    const pageTitle = useMemo(() => {
        if (isBank) return 'Bank account';
        if (isEps) return 'Card';
        if (isWallet) return 'Recipient';
        if (isPayBills) return 'Payee';
        if (isETransfer) return 'Recipient';
        return 'Recipient';
    }, [isBank, isEps, isWallet, isPayBills, isETransfer]);

    const addButtonData = useMemo(() => {
        if (isBank)
            return {
                icon: 'link',
                text: 'Link a bank account',
                onClick: () => navigate(routes.link_card_bank + '?type=bank')
            };
        if (isEps)
            return {
                icon: 'link',
                text: 'Link a card',
                onClick: () => navigate(routes.link_card_bank + '?type=card')
            };
        if (isWallet || isETransfer)
            return {
                icon: 'external_person',
                text:
                    method === EFundsOperation.WALLET_CREDIT_TRANSFER
                        ? 'Send to external user'
                        : 'Request from external user',
                onClick: () => {
                    setIsNewExternal(true);
                    onChangeNewContact && onChangeNewContact(true);
                    onChangeCustomContact && onChangeCustomContact(false);
                }
            };
        if (isPayBills)
            return {
                icon: 'add_payee',
                text: 'Add a new payee',
                onClick: () => navigate(routes.pay_bills_payees + '?isAdd=true')
            };
    }, [isBank, isEps, isWallet, isPayBills, isETransfer]);

    const searchPlaceholder = useMemo(() => {
        if (isBank) return 'account nickname or institution name...';
        if (isEps) return 'cardholder or card brand...';
        if (isWallet) return 'name, username, email, or phone...';
        if (isPayBills) return 'payee name, nickname or account number...';
        if (isETransfer) return 'name, username, email, or phone...';
        if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) return 'email address...';
        return pageTitle.toLowerCase() + '...';
    }, [isBank, isEps, isWallet, isPayBills, isETransfer, method]);

    const listTitle = useMemo(() => {
        if (isBank) return 'linked bank accounts';
        if (isEps) return 'linked cards';
        if (isWallet) return 'contacts';
        if (isPayBills) return 'payees';
        if (isETransfer) return 'contacts';
        return 'contacts';
    }, [isBank, isEps, isWallet, isPayBills, isETransfer]);

    const description = useMemo(() => {
        if (isBank) return 'Choose the bank account you need from your linked bank accounts';
        if (isEps) return 'Choose the credit or debit card you need from your linked cards';
        if (isWallet) return 'Choose the recipient from your contacts or XEPPT users';
        if (isPayBills) return 'Choose the payee you need from your payee list';
        if (isETransfer) return 'Choose the recipient from your contacts';
        if (method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD)
            return 'Add the recipient’s email or phone number to send the gift card for activation when needed. Recipient details can be added later.';
    }, [isBank, isEps, isWallet, isPayBills, isETransfer, method]);

    const externalDescription = useMemo(() => {
        if (method === EFundsOperation.WALLET_CREDIT_TRANSFER)
            return 'Enter the external user’s details to send them money and an invite email. They’ll receive the funds after registering and verifying their identity.';
        if (method === EFundsOperation.WALLET_DEBIT_REQUEST)
            return 'Enter the external user’s details to send them an email with a payment link';
    }, [method]);

    const onProcessFile = async (files: FormData[]) => {
        for (const file of files) {
            const emails = await processEmailsCsv(file);
            onChangeEmails &&
                onChangeEmails((state) => {
                    return [...state, ...emails];
                });
        }
    };

    const getEmailsCount = async (file: FormData | File) => {
        const emails = await processEmailsCsv(file);
        return `${emails?.length || 0} recipient emails detected`;
    };

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <div
                    className={styles.title}
                    dangerouslySetInnerHTML={{ __html: pageTitle || '' }}
                />
                <div className={styles.description}>
                    {isNewExternal ? externalDescription : description}
                </div>
            </div>
            {method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD ? (
                watchedIsBulkGiftCards ? (
                    <>
                        <div className={styles.search_wrapper}>
                            <div className={styles.uploader_wrapper}>
                                <Typography variant="label2">
                                    Recipient emails (optional)
                                </Typography>
                                <Uploader
                                    accept={['.csv']}
                                    size={10}
                                    onChange={onProcessFile}
                                    onProcessFileInfo={getEmailsCount}
                                    isHideDescription
                                    isOnlyFile
                                />
                            </div>
                            <InfoBlock variant="gray">
                                Upload a CSV file containing the email addresses of the recipients
                                for your gift cards. Please note: Upload a CSV file with one valid
                                recipient email per row. The file must contain only one column, no
                                duplicate emails, and no empty rows. The number of emails must match
                                the quantity of gift cards. You can download a CSV template here.
                            </InfoBlock>
                        </div>
                    </>
                ) : (
                    <div className={styles.recipient_type_wrapper}>
                        <div className={styles.radio_wrapper}>
                            <div
                                className={styles.radio}
                                onClick={() => setRecipientNotify('email')}>
                                <Radio checked={recipientNotify === 'email'} />
                                <Typography>Email address</Typography>
                            </div>
                            <div
                                className={styles.radio}
                                onClick={() => setRecipientNotify('phone')}>
                                <Radio checked={recipientNotify === 'phone'} />
                                <Typography>Phone number</Typography>
                            </div>
                        </div>
                        <div className={styles.search_wrapper}>
                            {recipientNotify === 'email' ? (
                                <FormField
                                    name="giftCardRecipientEmail"
                                    renderComponent={(props) => (
                                        <Input
                                            full
                                            placeholder="Enter email address"
                                            label="Recipient email (optional)"
                                            {...props}
                                        />
                                    )}
                                    rules={{
                                        validate: (value: string) => {
                                            if (value && !isValidEmail(value)) {
                                                return validationLocale('email');
                                            }
                                        }
                                    }}
                                />
                            ) : (
                                <Phone label="Recipient phone number (optional)" />
                            )}
                        </div>
                    </div>
                )
            ) : (
                !isNewExternal && (
                    <div className={styles.search_wrapper}>
                        <Input
                            full
                            value={search}
                            leftIcon="search"
                            rightIcon={search.length > 0 ? 'filed_close' : undefined}
                            onClickRightIcon={() => setSearch('')}
                            onChange={(val) => {
                                setSearch(val);
                                if (isETransfer && watchedContact) {
                                    setValue('contact', '');
                                }
                            }}
                            placeholder={`Search by ${searchPlaceholder}`}
                        />
                    </div>
                )
            )}
            {isNewExternal ? (
                <div className={styles.add_external}>
                    <FormField
                        name="new_contact.first_name"
                        renderComponent={(props) => (
                            <Input
                                label={labelLocale('first_name')}
                                full
                                {...props}
                                placeholder="Enter first name"
                            />
                        )}
                        rules={{
                            required: {
                                value: true,
                                message: validationLocale('first_name')
                            }
                        }}
                    />
                    <FormField
                        name="new_contact.last_name"
                        renderComponent={(props) => (
                            <Input
                                label={labelLocale('last_name')}
                                full
                                {...props}
                                placeholder="Enter last name"
                            />
                        )}
                        rules={{
                            required: {
                                value: true,
                                message: validationLocale('last_name')
                            }
                        }}
                    />
                    <FormField
                        name="new_contact.email"
                        renderComponent={(props) => (
                            <Input
                                label={labelLocale('email')}
                                full
                                {...props}
                                placeholder="Enter email"
                            />
                        )}
                        rules={{
                            required: {
                                value: true,
                                message: validationLocale('email')
                            }
                        }}
                    />
                    <FormField
                        name="new_contact.phone"
                        renderComponent={(props) => (
                            <Input
                                label={labelLocale('enter_contact_phone') + ' (optional)'}
                                full
                                {...props}
                                mask="1 (999) 999-9999"
                                placeholder="Enter phone number"
                            />
                        )}
                    />
                    <div className={styles.actions}>
                        <Button
                            variant="outlined-primary"
                            leftIcon="arrow_left"
                            onClick={() => setIsNewExternal(false)}>
                            Back to contacts
                        </Button>
                        <Button variant="primary" rightIcon="arrow_right" onClick={handleSubmit}>
                            Next
                        </Button>
                    </div>
                </div>
            ) : search &&
              contacts?.contacts?.length === 0 &&
              !contacts?.suggestions &&
              !foundContact ? (
                <div className={styles.empty_search_wrapper}>
                    <img src={emptySearchImage} alt="empty search illustration" />
                    <Typography weight="semibold" className={styles.title} variant="body3">
                        No results found
                    </Typography>
                    <Typography className={styles.description} variant="body4">
                        Please check if there are any spelling mistakes, or{' '}
                        {method === EFundsOperation.WALLET_CREDIT_TRANSFER
                            ? 'invite an external user to join XEPPT by sending them money'
                            : addButtonData?.text?.toLowerCase()}
                    </Typography>
                    <Button variant="gold" size="medium" onClick={addButtonData?.onClick}>
                        {addButtonData?.text}
                    </Button>
                </div>
            ) : (
                <>
                    {foundContact && !isETransfer && !search && (
                        <div
                            className={`${styles.contact} ${styles.isActive} ${styles.found_contact}`}
                            key={foundContact.id}>
                            <div className={styles.left}>
                                {!isBank && !isEps && !isPayBills && (
                                    <div className={styles.avatar}>
                                        {getFirstLetter(foundContact?.first_name)}
                                        {getFirstLetter(foundContact?.last_name)}
                                    </div>
                                )}
                                <div className={styles.text_wrapper}>
                                    <div
                                        className={styles.title}
                                        dangerouslySetInnerHTML={{
                                            __html: highlightSearchText(
                                                `${foundContact.first_name} ${foundContact.last_name}`,
                                                search
                                            )
                                        }}
                                    />
                                    <div
                                        className={styles.description}
                                        dangerouslySetInnerHTML={{
                                            __html: highlightSearchText(
                                                foundContact.email ||
                                                    foundContact?.username ||
                                                    //@ts-ignore
                                                    `+1${foundContact.phone}`,
                                                search
                                            )
                                        }}
                                    />
                                </div>
                            </div>
                            <Icon name="check" />
                        </div>
                    )}
                    <div className={styles.content}>
                        {((isETransfer && !watchedContact) || !isETransfer || isRequest) &&
                            addButtonData && (
                                <div className={styles.add_new} onClick={addButtonData.onClick}>
                                    <div className={styles.left}>
                                        <div className={styles.avatar}>
                                            <Icon name={addButtonData.icon as TIconType} />
                                        </div>{' '}
                                        {addButtonData.text}
                                    </div>
                                    <Icon className={styles.icon} name="nav_right" />
                                </div>
                            )}
                        <div className={styles.results_wrapper}>
                            {contacts?.contacts?.length > 0 && (
                                <div className={styles.result}>
                                    {((isETransfer && !watchedContact) || !isETransfer) && (
                                        <div
                                            className={styles.result_length}
                                            dangerouslySetInnerHTML={{
                                                __html: `Your ${listTitle}`
                                            }}
                                        />
                                    )}
                                    <FormField
                                        name={isBank || isEps || isPayBills ? 'account' : 'contact'}
                                        renderComponent={({ onChange }) => {
                                            return (
                                                <>
                                                    {contacts?.contacts?.map((contact) => {
                                                        if (
                                                            foundContact &&
                                                            contact.id === foundContact.id &&
                                                            !search &&
                                                            !isETransfer
                                                        )
                                                            return null;
                                                        return (
                                                            <div
                                                                className={`${styles.contact} ${contact.id === watchedContact || contact.id === watchedAccount ? styles.isActive : ''}`}
                                                                key={contact.id}
                                                                onClick={() => {
                                                                    onChange(contact.id);
                                                                    onChangeCustomContact &&
                                                                        onChangeCustomContact(
                                                                            false
                                                                        );
                                                                    onChangeNewContact &&
                                                                        onChangeNewContact(false);
                                                                    if (!isETransfer || isRequest) {
                                                                        onSubmit();
                                                                    }
                                                                }}>
                                                                <div className={styles.left}>
                                                                    {!isBank &&
                                                                        !isEps &&
                                                                        !isPayBills && (
                                                                            <div
                                                                                className={
                                                                                    styles.avatar
                                                                                }>
                                                                                {getFirstLetter(
                                                                                    contact?.first_name
                                                                                )}
                                                                                {getFirstLetter(
                                                                                    contact?.last_name
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    <div
                                                                        className={
                                                                            styles.text_wrapper
                                                                        }>
                                                                        <div
                                                                            className={styles.title}
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: highlightSearchText(
                                                                                    `${contact.first_name} ${contact.last_name}`,
                                                                                    search
                                                                                )
                                                                            }}
                                                                        />
                                                                        <div
                                                                            className={
                                                                                styles.description
                                                                            }
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: highlightSearchText(
                                                                                    contact.email ||
                                                                                        '',
                                                                                    search
                                                                                )
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {(contact.id === watchedContact ||
                                                                    contact.id ===
                                                                        watchedAccount) && (
                                                                    <Icon name="check" />
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            );
                                        }}
                                    />
                                </div>
                            )}
                            {method !== EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD &&
                                contacts?.contacts?.length === 0 &&
                                !search && (
                                    <div className={styles.result}>
                                        {((isETransfer && !watchedContact) || !isETransfer) && (
                                            <div
                                                className={styles.result_length}
                                                dangerouslySetInnerHTML={{
                                                    __html: `Your ${listTitle}`
                                                }}
                                            />
                                        )}
                                        <div className={styles.result}>
                                            <div className={styles.empty_wrapper}>
                                                <div className={styles.empty_icon}>
                                                    <Icon name="ghost" />
                                                </div>
                                                <p>
                                                    You don't have any{' '}
                                                    <span
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                method ===
                                                                EFundsOperation.EPS_DEBIT_DEPOSIT
                                                                    ? 'cards linked'
                                                                    : listTitle
                                                        }}
                                                    />
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            {contacts?.suggestions && contacts?.suggestions.length > 0 && (
                                <div className={styles.result}>
                                    {((isETransfer && !watchedContact) || !isETransfer) && (
                                        <div
                                            className={styles.result_length}
                                            dangerouslySetInnerHTML={{
                                                __html: 'XEPPT users'
                                            }}
                                        />
                                    )}
                                    <FormField
                                        name={isBank || isEps || isPayBills ? 'account' : 'contact'}
                                        renderComponent={({ onChange }) => {
                                            return (
                                                <>
                                                    {contacts?.suggestions?.map((contact) => {
                                                        return (
                                                            <div
                                                                className={`${styles.contact} ${contact.user_id === watchedContact || contact.user_id === watchedAccount ? styles.isActive : ''}`}
                                                                key={contact.user_id}
                                                                onClick={() => {
                                                                    onChange(contact.user_id);
                                                                    search &&
                                                                        onChangeNewContact &&
                                                                        onChangeNewContact(true);
                                                                    onChangeCustomContact &&
                                                                        onChangeCustomContact(
                                                                            false
                                                                        );
                                                                    if (!isETransfer || isRequest) {
                                                                        onSubmit();
                                                                    }
                                                                }}>
                                                                <div className={styles.left}>
                                                                    {!isBank &&
                                                                        !isEps &&
                                                                        !isPayBills && (
                                                                            <div
                                                                                className={
                                                                                    styles.avatar
                                                                                }>
                                                                                {getFirstLetter(
                                                                                    contact?.first_name
                                                                                )}
                                                                                {getFirstLetter(
                                                                                    contact?.last_name
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    <div
                                                                        className={
                                                                            styles.text_wrapper
                                                                        }>
                                                                        <div
                                                                            className={styles.title}
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: highlightSearchText(
                                                                                    `${contact.first_name} ${contact.last_name}`,
                                                                                    search
                                                                                )
                                                                            }}
                                                                        />
                                                                        <div
                                                                            className={
                                                                                styles.description
                                                                            }
                                                                            dangerouslySetInnerHTML={{
                                                                                __html: highlightSearchText(
                                                                                    `@${contact.username}`,
                                                                                    search
                                                                                )
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {(contact.user_id ===
                                                                    watchedContact ||
                                                                    contact.user_id ===
                                                                        watchedAccount) && (
                                                                    <Icon name="check" />
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            );
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        {isETransfer && watchedContact && (
                            <>
                                {isContactDdrLoading ? (
                                    <>
                                        <Skeleton className={styles.skeleton_info} />
                                        <Skeleton className={styles.skeleton} />
                                        <Skeleton className={styles.skeleton} />
                                    </>
                                ) : (
                                    <>
                                        {ddrContactStatus === EDdrStatus.ENABLED && (
                                            <InfoBlock variant="gray" className={styles.info}>
                                                <span
                                                    dangerouslySetInnerHTML={{
                                                        __html: t('autodeposit_title')
                                                    }}
                                                />{' '}
                                                -{' '}
                                                {t('autodeposit_description').replace(
                                                    '{{name}}',
                                                    `${foundContact?.first_name} ${foundContact?.last_name}`
                                                )}
                                            </InfoBlock>
                                        )}
                                        {ddrContactStatus === EDdrStatus.DISABLED && (
                                            <>
                                                <InfoBlock
                                                    variant="warning"
                                                    className={styles.info}>
                                                    {t('autodeposit_off')}
                                                </InfoBlock>
                                                <FormField
                                                    name="security_question"
                                                    renderComponent={(props) => (
                                                        <Input
                                                            className={styles.input}
                                                            full
                                                            label={labelLocale('security_question')}
                                                            placeholder={placeholderLocale(
                                                                'enter_security_question'
                                                            )}
                                                            {...props}
                                                        />
                                                    )}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message: validationLocale(
                                                                'invalid_security_question'
                                                            )
                                                        },
                                                        validate: (value: string) => {
                                                            const cleanValue = value.replaceAll(
                                                                ' ',
                                                                ''
                                                            );
                                                            if (cleanValue.length >= 40) {
                                                                return validationLocale(
                                                                    'invalid_security_question_length'
                                                                );
                                                            }
                                                        }
                                                    }}
                                                />
                                                <FormField
                                                    name="security_answer"
                                                    renderComponent={(props) => (
                                                        <Input
                                                            className={styles.input}
                                                            full
                                                            label={labelLocale('answer')}
                                                            placeholder={placeholderLocale(
                                                                'enter_answer'
                                                            )}
                                                            {...props}
                                                        />
                                                    )}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message:
                                                                validationLocale(
                                                                    'invalid_security_answer'
                                                                )
                                                        },
                                                        validate: (value: string) => {
                                                            const cleanValue = value.replaceAll(
                                                                ' ',
                                                                ''
                                                            );
                                                            if (
                                                                cleanValue.length <= 3 ||
                                                                cleanValue.length >= 25
                                                            ) {
                                                                return validationLocale(
                                                                    'invalid_security_answer_length'
                                                                );
                                                            }
                                                        }
                                                    }}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                    <div className={styles.actions}>
                        {method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD && (
                            <Button
                                leftIcon="arrow_left"
                                variant="outlined-primary"
                                onClick={() => {
                                    if (!!watchedContact) {
                                        setValue('contact', '');
                                    } else {
                                        onPrev && onPrev();
                                    }
                                }}>
                                {submitLocale('previous')}
                            </Button>
                        )}
                        {(watchedContact ||
                            watchedAccount ||
                            method === EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD) && (
                            <Button
                                onClick={handleSubmit}
                                rightIcon="arrow_right"
                                variant="primary">
                                {submitLocale('next')}
                            </Button>
                        )}
                    </div>
                </>
            )}
        </motion.div>
    );
};

export default Recipient;
